import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ExecutiveReportListResponse, ExecutiveReportRowDto, MeResponse } from '../../shared/api-types.js';
import { api } from '../api.js';
import { useRequest } from '../ui/use-request.js';
import { useMe } from './me-context.js';

// The Executive destination's count badge (ADR 0019; design §2.13: "a count badge on the Executive destination (and on
// More when folded) when a report newer than the executive's last open exists"; 02-patterns §1.4: a pill pinned to the
// icon's top-right, capped at 99+).
//
// Why one read of the report list, made once when the shell mounts and never on a route change: the badge is drawn on
// every page, and the list (`GET /api/executive/reports`) is the one route that says whether this executive opened each
// report WITHOUT recording an open — the report's own route records one (design §2.12), which would clear the very mark
// it was asked for. That list read is audited by the server at most once per executive and UTC day
// (`executive.reports.view`, the owner's ruling of 2026-10-08 on ADR 0019's Open 30, "Audit each read": "without touching
// the 'opened' flag or the 'newer than your last open' badge"). Nothing is prefetched for a section: every section of the
// module reads its own data when it is shown (the stage 6 brief).
//
// Why the pages tell the store of an open (`markOpened`): the server records an open when the Report section or the
// Overview card reads a report, and the badge must not keep counting a report the reader has just been shown.

/** As many reports as the list serves in one page (its PAGE_MAX): past 99 the badge reads 99+, so one page decides it. */
export const BADGE_PAGE = 100;

/** Whether the executive layer is offered to this person at all — the grant with its floor, and the floor on. Selection only. */
export function executiveOffered(me: Pick<MeResponse, 'capabilities' | 'integrations'>): boolean {
  return me.capabilities.executiveIntelligence && me.integrations.executive !== 'off';
}

/** A report the reader opened, as the pages learn it: its id and where its window ends (reports are ordered by it). */
export interface OpenedReport {
  id: string;
  windowEnd: string;
}

/**
 * How many readable reports are newer than the newest one this executive opened — those the list marks opened, or one a
 * page opened since the list was read. The list is latest first and no two windows overlap, so "newer" is a window ending
 * later. A first page of reports none of which was opened counts as the page, so 100 reads 99+.
 */
export function newReportCount(rows: readonly Pick<ExecutiveReportRowDto, 'id' | 'windowEnd' | 'opened'>[], openedHere: OpenedReport | null): number {
  const openedEnds = rows.filter((row) => row.opened || row.id === openedHere?.id).map((row) => row.windowEnd);
  if (openedHere) openedEnds.push(openedHere.windowEnd);
  const newestOpened = openedEnds.reduce<string | null>((latest, end) => (latest === null || end > latest ? end : latest), null);
  // Why compared as instants: the windows are ISO strings from one server clock, but a page may hold another offset.
  const after = newestOpened === null ? Number.NEGATIVE_INFINITY : Date.parse(newestOpened);
  return rows.filter((row) => Date.parse(row.windowEnd) > after).length;
}

/**
 * Whether the badge's reader has opened none of the reports it counts — no row of the page the list read marked opened,
 * and none opened on a page since. Why (the stage 6 review; 05-content: words say what is true): the count is then every
 * report on that page, and "since you last opened one" would claim an open that may never have happened — a newly granted
 * executive with months of reports kept has none; the badge's words say how many they have not opened instead.
 */
export function noReportOpened(rows: readonly Pick<ExecutiveReportRowDto, 'opened'>[], openedHere: OpenedReport | null): boolean {
  return openedHere === null && !rows.some((row) => row.opened);
}

export interface ExecutiveBadgeValue {
  /** Reports newer than the newest the reader opened; 0 when none, or when the layer is not offered or the read failed. */
  count: number;
  /** True when the reader has opened none of them (`noReportOpened`): the words change, never the count. */
  noneOpened: boolean;
  /** Tells the badge a report was opened (the server recorded the open with the read that showed it). */
  markOpened: (report: OpenedReport) => void;
}

const NONE: ExecutiveBadgeValue = { count: 0, noneOpened: false, markOpened: () => {} };

const ExecutiveBadgeContext = createContext<ExecutiveBadgeValue>(NONE);

/** The badge's count and the pages' way to clear it. Outside the provider (or for a non-executive) it is 0. */
export function useExecutiveBadge(): ExecutiveBadgeValue {
  return useContext(ExecutiveBadgeContext);
}

/**
 * Provides the badge to the shell. A person the layer is not offered to reads nothing: no request is made, the count is
 * 0. A failed read draws no badge — a missing count is not a wrong one, and the module itself says what failed.
 */
export function ExecutiveBadgeProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  if (!executiveOffered(me)) return <>{children}</>;
  return <LoadedExecutiveBadge>{children}</LoadedExecutiveBadge>;
}

function LoadedExecutiveBadge({ children }: { children: ReactNode }) {
  const page = useRequest<ExecutiveReportListResponse>((signal) => api.get<ExecutiveReportListResponse>(`/api/executive/reports?limit=${BADGE_PAGE}`, { signal }), []);
  const [openedHere, setOpenedHere] = useState<OpenedReport | null>(null);
  const markOpened = useCallback((report: OpenedReport) => {
    setOpenedHere((current) => (current === null || Date.parse(report.windowEnd) > Date.parse(current.windowEnd) ? report : current));
  }, []);
  const rows = page.data?.items ?? null;
  const value = useMemo<ExecutiveBadgeValue>(
    () => ({ count: rows ? newReportCount(rows, openedHere) : 0, noneOpened: rows ? noReportOpened(rows, openedHere) : false, markOpened }),
    [rows, openedHere, markOpened],
  );
  return <ExecutiveBadgeContext.Provider value={value}>{children}</ExecutiveBadgeContext.Provider>;
}
